import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MessageSquare, ArrowRight, Loader2, AlertCircle, Check } from 'lucide-react';
import { resolveHook, hookHeadline } from '../data/flyerHooks';
import {
  LAUNCH_PROMO_CODE,
  LAUNCH_PROMO_CAP,
  FALLBACK_PROMO_CODE,
  SEAT_PROMO_CODE,
  PROMO_EXPIRY_ISO,
  PROMO_EXPIRY_DISPLAY,
} from '../data/constants';
import { getAttribution, trackEvent } from '../utils/metaTracking';

// Flyer QR landing — one page, many doors. Every flyer QR encodes
// /flyer?h=<keyword>; this page shows that flyer's headline (src/data/flyerHooks.js),
// logs the scan, and hands the hook to the booking so it attributes back.
// Offer state comes from the live MONARCH15 counter + the shared promo end date,
// so the page never shows a dead offer.

const GOLD = '#D4AF37';
const SHOP_PHONE_SMS = '+13018859767';
const SHOP_PHONE_DISPLAY = '(301) 885-9767';
const PROMO_END_MS = new Date(PROMO_EXPIRY_ISO).getTime();

const VEHICLES = [
  { id: 'car', label: 'Car' },
  { id: 'suv', label: 'SUV' },
  { id: 'truck', label: 'Truck' },
];

const phoneLooksValid = (value) => {
  const digits = value.replace(/\D/g, '');
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
};

const fieldCls = (bad) =>
  `w-full rounded-xl px-4 py-3.5 bg-[#111114] text-white placeholder-gray-500 border-2 focus:outline-none text-base ${
    bad ? 'border-red-500' : 'border-[#2a2a30] focus:border-[#D4AF37]'
  }`;

// One scan log per page load (not per re-render, not per in-app navigation).
let scanLoggedThisLoad = false;

export default function FlyerPage() {
  const [searchParams] = useSearchParams();
  const rawHook = searchParams.get('h');
  const hook = resolveHook(rawHook); // known keyword or 'unknown'
  const headline = hookHeadline(hook);
  const keyword = (hook === 'unknown' ? 'newcar' : hook).toUpperCase();

  // ── offer state ───────────────────────────────────────────────────────────
  const promoOpen = Date.now() <= PROMO_END_MS;
  const [status, setStatus] = useState(null); // { soldOut, remaining } from the live counter
  useEffect(() => {
    if (!promoOpen) return undefined;
    let cancelled = false;
    fetch('/api/promo-status')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled && data?.launch) setStatus(data.launch); })
      .catch(() => {}); // fail open: show the offer, hide the count
    return () => { cancelled = true; };
  }, [promoOpen]);
  const soldOut = !!status?.soldOut;
  const activeCode = !promoOpen ? null : soldOut ? FALLBACK_PROMO_CODE : LAUNCH_PROMO_CODE;

  // ── scan log (anonymous) ──────────────────────────────────────────────────
  useEffect(() => {
    if (scanLoggedThisLoad) return;
    scanLoggedThisLoad = true;
    const utm = getAttribution();
    fetch('/api/ai', {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'flyer-event',
        hook,
        hookRaw: rawHook || '',
        referer: document.referrer || '',
        utm_source: utm.utm_source || '', utm_medium: utm.utm_medium || '',
        utm_campaign: utm.utm_campaign || '', utm_content: utm.utm_content || '',
      }),
    }).catch(() => {});
  }, [hook, rawHook]);

  // ── booking link ──────────────────────────────────────────────────────────
  // Car seat is OPT-IN: off by default, only added when the visitor turns it on.
  const [carSeat, setCarSeat] = useState(false);
  const bookUrl = useMemo(() => {
    const q = new URLSearchParams({ package: 'interior' });
    if (activeCode) q.set('promo', activeCode);
    q.set('hook', hook);
    if (carSeat) {
      q.set('addons', 'car_seat');
      if (promoOpen) q.set('seatpromo', SEAT_PROMO_CODE);
    }
    return `/detail/book?${q.toString()}`;
  }, [activeCode, hook, carSeat, promoOpen]);

  const smsHref = `sms:${SHOP_PHONE_SMS}?&body=${encodeURIComponent(keyword)}`;

  // ── warm-lead form ────────────────────────────────────────────────────────
  const [form, setForm] = useState({ name: '', phone: '', vehicle: '', town: '', preferredDay: '', website: '' });
  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const [done, setDone] = useState(false);
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const errors = {
    name: !form.name.trim(),
    phone: !phoneLooksValid(form.phone),
    vehicle: !form.vehicle,
    town: !form.town.trim(),
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setServerError('');
    if (Object.values(errors).some(Boolean)) { setShowErrors(true); return; }
    setSubmitting(true);
    try {
      const utm = getAttribution();
      const res = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'flyer-lead',
          name: form.name.trim(), phone: form.phone.trim(), vehicle: form.vehicle,
          town: form.town.trim(), preferredDay: form.preferredDay.trim(), website: form.website, hook,
          utm_source: utm.utm_source || '', utm_medium: utm.utm_medium || '',
          utm_campaign: utm.utm_campaign || '', utm_content: utm.utm_content || '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Something went wrong. Please text Akeem at ${SHOP_PHONE_DISPLAY}.`);
      setDone(true);
    } catch (err) {
      setServerError(err.message || `Something went wrong. Please text Akeem at ${SHOP_PHONE_DISPLAY}.`);
    } finally {
      setSubmitting(false);
    }
  };

  // ── pieces ────────────────────────────────────────────────────────────────
  const BookButton = () => (
    <Link
      to={bookUrl}
      className="flex items-center justify-center gap-2 w-full bg-[#D4AF37] text-black font-black text-lg py-4 rounded-2xl hover:brightness-110 transition-all"
    >
      Book my detail <ArrowRight size={20} />
    </Link>
  );

  const offerPct = soldOut ? 10 : 15;

  return (
    <div className="min-h-screen bg-[#08080a] text-white">
      <div className="max-w-md mx-auto px-5 pb-14">
        {/* Header */}
        <div className="text-center pt-7">
          <img
            src="/images/logo-sky-circle.png"
            alt="Monarch Detailing"
            className="h-24 w-24 rounded-full object-cover mx-auto mb-3 border-4"
            style={{ borderColor: GOLD }}
          />
          <p className="font-bold tracking-[0.2em] text-xs" style={{ color: GOLD }}>MONARCH DETAILING</p>
          <p className="text-[10px] tracking-[0.3em] text-gray-400 mt-1">MOBILE INTERIOR DETAILING</p>
        </div>

        {/* Hook headline — message match with the flyer that was scanned */}
        <h1 className="text-[1.9rem] leading-tight font-black text-center mt-6" style={{ color: GOLD }} data-hook={hook}>
          {headline}
        </h1>
        <p className="text-center text-sm text-gray-400 mt-3">
          Interior detailing · We come to you in St. Mary's, Charles &amp; Calvert County
        </p>

        {/* Offer */}
        <div className="mt-6 rounded-2xl border-2 p-5 text-center" style={{ borderColor: GOLD }}>
          {activeCode ? (
            <>
              <p className="text-2xl font-black text-white">{offerPct}% off any interior detail</p>
              {!soldOut && status && status.remaining !== null && status.remaining !== undefined && (
                <p className="text-sm font-bold mt-1" style={{ color: '#FF7A1A' }}>
                  {status.remaining} of {LAUNCH_PROMO_CAP} left
                </p>
              )}
              <p className="text-sm text-gray-300 mt-2">Car seat cleans 50% off ($17.50) with any detail</p>
              <p className="text-[11px] text-gray-500 mt-2">Applied automatically when you book · ends {PROMO_EXPIRY_DISPLAY}</p>
            </>
          ) : (
            <>
              <p className="text-xl font-black text-white">Interior details from $75</p>
              <p className="text-sm text-gray-300 mt-1">Mobile — we come to your home or work. 50% deposit books your day.</p>
            </>
          )}
        </div>

        {/* Car seat — OPT-IN, default off */}
        <button
          type="button"
          role="switch"
          aria-checked={carSeat}
          onClick={() => setCarSeat((v) => !v)}
          className={`mt-4 w-full flex items-center justify-between gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
            carSeat ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#2a2a30] bg-[#111114]'
          }`}
        >
          <span className="text-sm font-bold text-gray-100">
            👶 Add a car seat clean {promoOpen ? '— 50% off ($17.50)' : '($35)'}
          </span>
          <span className={`w-11 h-6 rounded-full relative shrink-0 transition-all ${carSeat ? 'bg-[#D4AF37]' : 'bg-gray-600'}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${carSeat ? 'left-[22px]' : 'left-0.5'}`} />
          </span>
        </button>

        {/* Primary + secondary actions */}
        <div className="mt-4 space-y-3">
          <BookButton />
          <a
            href={smsHref}
            onClick={() => trackEvent('Contact', { content_name: 'flyer_text_me', content_category: hook })}
            className="flex items-center justify-center gap-2 w-full border-2 border-[#D4AF37] text-[#D4AF37] font-black text-lg py-3.5 rounded-2xl"
          >
            <MessageSquare size={20} /> Text me instead
          </a>
          <p className="text-center text-xs text-gray-500">Opens your texts with “{keyword}” ready to send.</p>
        </div>

        {/* Warm-lead form */}
        <div className="mt-10 pt-8 border-t border-[#2a2a30]">
          {done ? (
            <div className="text-center" role="status">
              <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: GOLD }}>
                <Check size={24} className="text-black" strokeWidth={3} />
              </div>
              <p className="text-2xl font-black mb-2" style={{ color: GOLD }}>Got it — I'll text you today.</p>
              <p className="text-gray-400 text-sm mb-5">Or grab your spot now:</p>
              <BookButton />
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate aria-label="Hold the offer and text me">
              <p className="text-lg font-black text-white">Not ready? I'll hold the offer and text you.</p>
              <p className="text-sm text-gray-400 mb-4">Takes 20 seconds.</p>

              {/* Honeypot */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
                <label>Website <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set({ website: e.target.value })} /></label>
              </div>

              <div className="space-y-3">
                <div>
                  <input id="fl-name" aria-label="First name" type="text" autoComplete="given-name" value={form.name} onChange={(e) => set({ name: e.target.value })}
                    className={fieldCls(showErrors && errors.name)} placeholder="First name" maxLength={60} />
                  {showErrors && errors.name && <p className="text-red-400 text-sm mt-1">Please enter your first name.</p>}
                </div>
                <div>
                  <input id="fl-phone" aria-label="Mobile number" type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={(e) => set({ phone: e.target.value })}
                    className={fieldCls(showErrors && errors.phone)} placeholder="Mobile number" maxLength={20} />
                  {showErrors && errors.phone && <p className="text-red-400 text-sm mt-1">Enter a valid 10-digit mobile number.</p>}
                </div>
                <div>
                  <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Vehicle">
                    {VEHICLES.map((v) => (
                      <button key={v.id} type="button" role="radio" aria-checked={form.vehicle === v.id} onClick={() => set({ vehicle: v.id })}
                        className={`py-3 rounded-xl border-2 font-bold text-sm transition-all ${
                          form.vehicle === v.id ? 'bg-[#D4AF37] border-[#D4AF37] text-black'
                            : `bg-[#111114] text-gray-200 ${showErrors && errors.vehicle ? 'border-red-500' : 'border-[#2a2a30]'}`
                        }`}>
                        {v.label}
                      </button>
                    ))}
                  </div>
                  {showErrors && errors.vehicle && <p className="text-red-400 text-sm mt-1">Pick your vehicle type.</p>}
                </div>
                <div>
                  <input id="fl-town" aria-label="Town" type="text" autoComplete="address-level2" value={form.town} onChange={(e) => set({ town: e.target.value })}
                    className={fieldCls(showErrors && errors.town)} placeholder="Town (e.g. Lexington Park)" maxLength={60} />
                  {showErrors && errors.town && <p className="text-red-400 text-sm mt-1">Which town are you in?</p>}
                </div>
                <input id="fl-day" aria-label="Preferred day (optional)" type="text" value={form.preferredDay} onChange={(e) => set({ preferredDay: e.target.value })}
                  className={fieldCls(false)} placeholder="Preferred day (optional)" maxLength={60} />
              </div>

              {serverError && (
                <div role="alert" className="mt-3 flex items-start gap-2 bg-red-950/60 border border-red-500/60 rounded-xl p-3 text-sm text-red-200">
                  <AlertCircle size={18} className="shrink-0 mt-0.5" /> <span>{serverError}</span>
                </div>
              )}

              <button type="submit" disabled={submitting}
                className="mt-4 w-full flex items-center justify-center gap-2 font-black text-base py-3.5 rounded-2xl border-2 border-[#D4AF37] text-[#D4AF37] disabled:opacity-60">
                {submitting ? (<><Loader2 size={18} className="animate-spin" /> Sending…</>) : 'Hold my offer & text me'}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-gray-500 mt-10">
          Akeem · <a href={`sms:${SHOP_PHONE_SMS}`} className="underline">Text</a> or <a href={`tel:${SHOP_PHONE_SMS}`} className="underline">call</a> {SHOP_PHONE_DISPLAY}
          <br />
          <Link to="/privacy" className="underline">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
}
